"use client";

import "./subscription-page.css";
import Header from "../header/page";
import Skeleton from "react-loading-skeleton";
import { useEffect, useRef, useState } from "react";
import { useSubscriptionContext } from "./subscriptionContext";
import BenefitsModal from "@/app/shared-components/BenefitsModal";
import ChevronsRightIcon from "../../shared-components/icons/ChevronsRightIcon";
import RenewSubscriptionModal from "./renewSubscriptionModal";
import PastDueInvoicesList from "./invoice/PastDueInvoicesList";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { publishModalVisibility } from "../../redux/features/modal/modalSlice";
import Modal from "../../components/Modal/modal";
import CompetitionsSection from "@/app/(custom-header)/botb-pass-members-home/components/CompetitionsSection";
import MemberHero from "./components/MemberHero";
import FloatingUnusedTicketsBar from "./components/FloatingUnusedTicketsBar";
import { useGetBotbPassMembersDataQuery } from "@/app/redux/features/api/membersHome";

const SubscriptionPageContent = () => {
  const ref = useRef<HTMLDivElement>();
  const walletBarRef = useRef<HTMLDivElement>();
  const [minimumheight, setMinimumHeight] = useState<string>();
  const {
    isSubscriptionLoading,
    isBenefitsModalOpen,
    setIsBenefitsModalOpen,
    isRenewSubscriptionModalOpen,
    setIsRenewSubscriptionModalOpen,
  } = useSubscriptionContext();
  const { data: membersData } = useGetBotbPassMembersDataQuery();
  const hasUnusedTickets = membersData?.UnusedTickets ?? true;
  const dispatch = useAppDispatch();
  const isModalOpen = useAppSelector(state => state.modal.isOpen);
  const modalData = useAppSelector(state => state.modal.data);
  const modalOverlay = useAppSelector(state => state.modal.withOverlay);

  useEffect(() => {
    if (ref.current) {
      setMinimumHeight(`calc(100vh - ${ref.current.getBoundingClientRect().top}px)`);
    }
  }, [ref.current]);

  useEffect(() => {
    if (isSubscriptionLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("renew") === "1") {
      setIsRenewSubscriptionModalOpen(true);
    }
  }, [isSubscriptionLoading]);

  const redirectWalletUrl = "/account/my-wallet";

  return (
    <>
      <BenefitsModal isOpen={isBenefitsModalOpen} onClose={() => setIsBenefitsModalOpen(false)} />
      {isRenewSubscriptionModalOpen && <RenewSubscriptionModal />}
      <header className="sticky top-0 bg-white z-[1003] botb-pass-header">
        <Header renderModalComponentHere={false} isBotBPassHeader={true} />
      </header>
      <article className="mainSection mx-auto flex px-3 lg:px-[1.5rem]">
        <section
          className="leftSection w-[24%] lg:shrink-0 relative mt-12 max-lg:hidden"
          style={{ minHeight: minimumheight }}
          ref={ref}>
          <h3 className="normal-case text-left text-lg lg:text-2xl font-semibold leading-[1.5rem] lg:leading-[1.875rem] !mb-0 text-dark-1">
            BOTB Pass
          </h3>
        </section>
        <section className="rightSection subscription-right-panel relative overflow-hidden lg:shadow-[-15px_4px_20px_-10px_rgba(0,0,0,0.10)] lg:w-[76%] lg:shrink-0 pb-28 lg:pb-12">
          {isSubscriptionLoading ? (
            <div className="mt-6 lg:mt-12 px-4 lg:px-8">
              <Skeleton
                count={1}
                inline
                highlightColor="#fff"
                containerClassName="flex flex-col w-full h-[500px]"
                className="h-full"
              />
            </div>
          ) : (
            <>
              <div
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-[280px] lg:h-[322px] pointer-events-none bg-[linear-gradient(to_top,rgba(229,206,246,0)_0%,#e5cef6_100%)]"
              />
              <img
                src="/assets/images/subscriptions/botb-pass-symbol.webp"
                alt=""
                className="absolute -top-5 -right-8 lg:-top-8 lg:-right-10 w-[104px] lg:w-[168px] pointer-events-none select-none z-0"
              />

              <div className="subscription-right-inner relative">
                <MemberHero />

                <div className="flex flex-col gap-6 px-4 lg:px-8 mt-2 lg:mt-4">
                  <PastDueInvoicesList />

                  <div
                    ref={walletBarRef}
                    data-name="wallet"
                    className="flex justify-between items-center border border-black/8 rounded-2xl p-3 lg:p-4">
                    <div className="flex items-center gap-2 lg:gap-3 min-w-0">
                      <img
                        src={
                          hasUnusedTickets
                            ? "/assets/images/subscriptions/wallet-tickets.webp"
                            : "/assets/images/botb-pass-members/trophy.webp"
                        }
                        alt={hasUnusedTickets ? "Wallet with tickets" : "Trophy"}
                        width={40}
                        height={40}
                        className="size-10 lg:size-14 aspect-square shrink-0"
                      />
                      <div className="flex flex-col min-w-0">
                        <div className="text-secondary font-jost text-sm lg:text-xl font-medium leading-5 lg:leading-6.5">
                          {hasUnusedTickets ? "Play your unused tickets" : "All your Pass entries are in"}
                        </div>
                        <div className="text-gray-1 font-jost text-xs font-normal leading-4">
                          {hasUnusedTickets
                            ? "Your next win could be one ticket away"
                            : "Boost your odds with extra tickets"}
                        </div>
                      </div>
                    </div>
                    <a
                      href={hasUnusedTickets ? redirectWalletUrl : "/#featured"}
                      title={hasUnusedTickets ? "View wallet" : "View competitions"}
                      className="flex items-center gap-2 text-primary font-jost text-sm lg:text-base font-medium uppercase whitespace-nowrap shrink-0">
                      {hasUnusedTickets ? "View wallet" : "View competitions"}
                      <ChevronsRightIcon variant={2} className="size-4 shrink-0 text-primary max-lg:hidden" />
                    </a>
                  </div>
                </div>

                <div className="mt-8 lg:mt-12">
                  <CompetitionsSection bordered />
                </div>

                <div className="px-4 lg:px-8 mt-8 lg:mt-10 flex justify-center lg:justify-start">
                  <a
                    href="/management-subscription"
                    title="Manage your botb pass"
                    className="theme-btn theme-btn-primary-outline theme-btn-2lg uppercase text-primary w-full max-w-[245px] lg:w-auto lg:max-w-none lg:min-w-[284px]">
                    Manage your botb pass
                  </a>
                </div>
              </div>

              <FloatingUnusedTicketsBar observeRef={walletBarRef} hasUnusedTickets={hasUnusedTickets} />
            </>
          )}
        </section>
      </article>
      <Modal
        isOpen={isModalOpen}
        body={modalData}
        title=""
        withOverlay={modalOverlay}
        closeModal={() => dispatch(publishModalVisibility({ isOpen: false }))}
      />
    </>
  );
};

export default SubscriptionPageContent;
